import AddIcon from "@mui/icons-material/Add";
import clsx from "clsx";
import React, { useCallback, useEffect, useState } from "react";
import { IPage } from "api/entities/page";

import { getDefaultRequestParams, getEmptyPage } from "redux/reducers/entity";
import { FormMode } from "utils/form-utils";
import { useApi } from "utils/hooks/use-api";
import IComponentProps from "components/shared-ui/common/component-props";
import Button from "components/shared-ui/button";
import Radio from "components/shared-ui/radio/radio";
import Table, { IColumnDefinition } from "components/shared-ui/table/table";
import {
  CredentialUserSectionFilters,
  UserListFilter,
} from "components/access-credentials/filters/credential-panel-filters/credential-user-section-filters";
import styles from "./credentials-user-section.module.scss";
import { IAMUserSortField, IIAMUser } from "api/entities/iam-users";
import { iamService } from "api/services/iam";
import { IIAMEntityRequestParams } from "api/entities/iam-entity";
import IAMUserPanel from "components/iam-users/user-panel/user-panel";

interface ICredentialsUserSectionProps extends IComponentProps {
  filter?: UserListFilter;
  visibleFilters?: UserListFilter[];
  onSelectUserName?: (UserName: string) => void;
  selectedUserName: string;
  autoHidePaging?: boolean;
}

export const CredentialsUserSection = (props: ICredentialsUserSectionProps) => {
  const [userPage, setUserPage] = useState<IPage<IIAMUser>>(getEmptyPage());
  const [api, loading] = useApi();

  const [searchParams, setSearchParams] = useState<
    IIAMEntityRequestParams<IAMUserSortField>
  >({ ...getDefaultRequestParams() });

  const [searchText, setSearchText] = useState<string>(null);

  function loadData(params?: IIAMEntityRequestParams<IAMUserSortField>) {
    api(iamService.getUsers(params)).then(setUserPage);
  }

  const columns: IColumnDefinition<IIAMUser, IAMUserSortField>[] = [
    {
      className: styles["col--radio"],
      formatter: (row) => (
        <Radio
          className={styles.radio}
          name="userName"
          onClick={() => props.onSelectUserName(row.UserName)}
          checked={row.UserName === props.selectedUserName}
        />
      ),
    },
    {
      sortKey: IAMUserSortField.UserName,
      label: "User Name",
      className: styles["col--user-name"],
      formatter: (row) => row.UserName,
    } as IColumnDefinition<IIAMUser, IAMUserSortField>,
  ];

  function updateFilters(searchString: string) {
    setSearchText(searchString);
  }

  function updateSearchParams(sp: IIAMEntityRequestParams<IAMUserSortField>) {
    setSearchParams((searchParams) => ({
      ...searchParams,
      ...sp,
    }));
  }

  const [userPanelMode, setUserPanelMode] = useState<FormMode>(null);

  function handleAddUserFormPanelOpen() {
    setUserPanelMode(FormMode.Create);
  }

  function handleAddUserFormPanelClose() {
    setUserPanelMode(null);
  }

  const filteredPage = useCallback(() => {
    return searchText && searchText.length > 0
      ? userPage.data.filter((u) => u.UserName.includes(searchText))
      : userPage.data;
  }, [userPage, searchText]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Button
        testId="add-userButton-button"
        className={styles["btn-add-bucket"]}
        colors="primary"
        onClick={handleAddUserFormPanelOpen}
      >
        <AddIcon />
        Add User
      </Button>

      <div
        className={clsx(styles.border, props.className)}
        data-testid={props.testId || "users-list"}
      >
        <CredentialUserSectionFilters
          filteredText={searchText}
          visibleFilters={props.visibleFilters}
          onFiltersChange={updateFilters}
        />
        <Table
          autoHidePaging={props.autoHidePaging}
          className={styles.table}
          compact={true}
          columns={columns}
          rows={filteredPage()}
          loading={loading}
          totalRows={userPage.count}
          testId="user-table"
          requestParams={searchParams}
          onRequestParamsChanged={updateSearchParams}
          hideCheckbox={true}
        />
      </div>

      <IAMUserPanel
        mode={userPanelMode}
        embedded={true}
        fetchUsers={() => loadData()}
        onClose={handleAddUserFormPanelClose}
      />
    </>
  );
};
